import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../components/ui/accordion';
import API from '../lib/api';
import NotificationBell from './NotificationBell';
import DownloadAppButton from './DownloadAppButton';
import { Logo1ClinIcon } from './Logo1Clin';
import { toast } from 'sonner';
import { requestNotificationPermission, registerPushNotifications } from '../lib/pushNotifications';
import {
  LayoutDashboard, Calendar, Users, Stethoscope,
  IndianRupee, Mic, Activity, Settings, LogOut,
  Menu, X, History, Store, HelpCircle, Package, Wallet, CreditCard, Crown, Lock, AlertTriangle, Video, Heart, Receipt, FlaskConical
} from 'lucide-react';

// Feature ID to path mapping
const featurePathMap = {
  'dashboard': '/doctor/dashboard',
  'appointments': '/doctor/appointments',
  'patients': '/doctor/patients',
  'clinical': '/doctor/clinical',
  'billing': '/doctor/billing',
  'ai_scribe': '/doctor/ai-scribe',
  'marketplace': '/doctor/marketplace',
  'labs_store': '/doctor/labs-store',
  'inventory': '/doctor/inventory',
  'wallet': '/doctor/wallet',
  'expenses': '/doctor/expenses',
  'mr_visitors': '/doctor/mr-visitors',
  'patient_history': '/doctor/patient-history',
  'analytics': '/doctor/analytics',
  'face_vitals': '/doctor/face-vitals',
  'health_tracking': '/doctor/health-tracking',
  'marketing_growth': '/doctor/marketing',
  'online_consultation': '/doctor/online-consultation',
  'vendor_bills': '/doctor/vendor-bills',
  'settings': '/doctor/settings',
  'support': '/doctor/support'
};

const navItems = [
  { path: '/doctor/dashboard', label: 'Dashboard', icon: LayoutDashboard, featureId: 'dashboard' },
  { path: '/doctor/appointments', label: 'Appointments', icon: Calendar, featureId: 'appointments' },
  { path: '/doctor/patients', label: 'Patient Directory', icon: Users, featureId: 'patients' },
  { path: '/doctor/clinical', label: 'Consultation Hub', icon: Stethoscope, featureId: 'clinical' },
  { path: '/doctor/billing', label: 'Quick Billing', icon: IndianRupee, featureId: 'billing' },
  { path: '/doctor/ai-scribe', label: 'AI Scribe', icon: Mic, featureId: 'ai_scribe' },
  { path: '/doctor/marketplace', label: 'B2B Marketplace', icon: Store, featureId: 'marketplace' },
  { path: '/doctor/labs-store', label: 'Labs & Store', icon: FlaskConical, featureId: 'labs_store' },
  { path: '/doctor/inventory', label: 'Inventory', icon: Package, featureId: 'inventory' },
  { path: '/doctor/wallet', label: 'Wallet & Payouts', icon: CreditCard, featureId: 'wallet' },
  { path: '/doctor/subscriptions', label: 'Subscription', icon: Crown, featureId: 'subscription' },
  { path: '/doctor/expenses', label: 'Expense Ledger', icon: Wallet, featureId: 'expenses' },
  { path: '/doctor/mr-visitors', label: 'MR Visitors', icon: Users, featureId: 'mr_visitors' },
  { path: '/doctor/analytics', label: 'Performance Analytics', icon: Activity, featureId: 'analytics' },
  { path: '/doctor/face-vitals', label: 'Face Vitals Kiosk', icon: Heart, featureId: 'face_vitals' },
  { path: '/doctor/health-tracking', label: 'Health Tracking', icon: Activity, featureId: 'health_tracking' },
  { path: '/doctor/marketing', label: 'Marketing Channels', icon: Activity, featureId: 'marketing_growth' },
  { path: '/doctor/online-consultation', label: 'Online Consult', icon: Video, featureId: 'online_consultation' },
  { path: '/doctor/vendor-bills', label: 'Connected Vendor Bills', icon: Receipt, featureId: 'vendor_bills' },
  { path: '/doctor/settings', label: 'System Settings', icon: Settings, featureId: 'settings' },
  { path: '/doctor/support', label: 'Support Desk', icon: HelpCircle, featureId: 'support' },
];

// 4-category accordion grouping (Phase 1 nav consolidation) - purely a display grouping,
// featureId/path/permission-gating logic below is completely untouched.
const navGroupDefs = [
  { id: 'clinical_suite', label: 'Clinical Suite', icon: Stethoscope, featureIds: ['dashboard', 'appointments', 'clinical', 'patients', 'face_vitals', 'health_tracking', 'online_consultation'] },
  { id: 'practice_financials', label: 'Practice Financials', icon: IndianRupee, featureIds: ['billing', 'inventory', 'expenses', 'vendor_bills', 'wallet', 'subscription'] },
  { id: 'practice_growth', label: 'Practice Growth', icon: Activity, featureIds: ['analytics', 'marketing_growth', 'marketplace', 'labs_store', 'mr_visitors'] },
  { id: 'configuration', label: 'Configuration', icon: Settings, featureIds: ['settings', 'support'] },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [permissions, setPermissions] = useState(null);
  const [subscriptionFeatures, setSubscriptionFeatures] = useState(null);
  const [subscriptionTier, setSubscriptionTier] = useState(null); // 'basic', 'smart', 'advance'
  const [subscriptionPlanName, setSubscriptionPlanName] = useState(null);
  const [loadingPermissions, setLoadingPermissions] = useState(true);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);

  // Which accordion nav group contains the current route - opened by default
  const activeGroupId = navGroupDefs.find(g =>
    navItems.some(item => g.featureIds.includes(item.featureId) &&
      (location.pathname === item.path || location.pathname === item.path.replace('/doctor', '')))
  )?.id || 'clinical_suite';
  const [openGroups, setOpenGroups] = useState([activeGroupId]);

  const [subscriptionStatus, setSubscriptionStatus] = useState(null); // 'active', 'expired', 'none'

  // Handle logout with confirmation
  const handleLogoutClick = useCallback(() => {
    setShowLogoutDialog(true);
  }, []);

  const confirmLogout = useCallback(() => {
    setShowLogoutDialog(false);
    logout();
    navigate('/doctor/login');
    toast.success('Logged out successfully');
  }, [logout, navigate]);

  const cancelLogout = useCallback(() => {
    setShowLogoutDialog(false);
  }, []);

  // Prevent back button from logging out - push state to history
  useEffect(() => {
    // Push a state to prevent immediate back navigation to login
    const handlePopState = (event) => {
      // If user presses back and would go to login page, prevent it
      if (user && window.location.pathname.includes('/doctor/')) {
        // Push current state back to prevent leaving the app
        window.history.pushState(null, '', window.location.pathname);
      }
    };

    // Initial push to prevent back from going to login
    if (user) {
      window.history.pushState(null, '', window.location.pathname);
      window.addEventListener('popstate', handlePopState);
    }

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [user]);

  // Fetch permissions for staff members AND subscription features for doctors
  useEffect(() => {
    const fetchPermissions = async () => {
      if (user?.role === 'doctor') {
        // For doctors, fetch subscription-based features
        try {
          const subRes = await API.get('/subscriptions/my-subscription');
          const subscription = subRes.data?.subscription;
          
          if (subscription) {
            // Check if subscription is expired - expires_at is stored as UTC
            const expiresStr = subscription.expires_at;
            const expiresAt = new Date(expiresStr.endsWith('Z') ? expiresStr : expiresStr + 'Z');
            const now = new Date();
            const isExpired = expiresAt < now;
            
            if (isExpired) {
              // Subscription expired - only allow wallet and subscription access
              setSubscriptionStatus('expired');
              setSubscriptionFeatures(null);
              setSubscriptionTier(subscription.tier);
              setSubscriptionPlanName(subscription.plan_name);
              const expiredPerms = {};
              navItems.forEach(item => {
                // Only allow wallet and subscription when expired
                const allowedWhenExpired = ['wallet', 'subscription', 'settings', 'support', 'labs_store'];
                expiredPerms[item.featureId] = allowedWhenExpired.includes(item.featureId);
              });
              setPermissions(expiredPerms);
            } else {
              // Active subscription - use subscription features
              setSubscriptionStatus('active');
              setSubscriptionTier(subscription.tier);
              setSubscriptionPlanName(subscription.plan_name);
              const features = subscription.features || {};
              setSubscriptionFeatures(features);
              const perms = {};
              navItems.forEach(item => {
                // Always allow these core features
                const alwaysAllowed = ['wallet', 'subscription', 'settings', 'support', 'labs_store'];
                if (alwaysAllowed.includes(item.featureId)) {
                  perms[item.featureId] = true;
                } else {
                  // Check if feature is enabled in subscription
                  perms[item.featureId] = features[item.featureId] === true;
                }
              });
              setPermissions(perms);
            }
          } else {
            // No subscription - only wallet, subscription, settings, support allowed
            setSubscriptionStatus('none');
            setSubscriptionFeatures(null);
            const noSubPerms = {};
            navItems.forEach(item => {
              // Before subscription, only allow these
              const allowedBeforeSub = ['wallet', 'subscription', 'settings', 'support', 'labs_store'];
              noSubPerms[item.featureId] = allowedBeforeSub.includes(item.featureId);
            });
            setPermissions(noSubPerms);
          }
        } catch (err) {
          console.error('Failed to fetch subscription features', err);
          // Default to minimal access on error
          setSubscriptionStatus('none');
          const noSubPerms = {};
          navItems.forEach(item => {
            const allowedBeforeSub = ['wallet', 'subscription', 'settings', 'support', 'labs_store'];
            noSubPerms[item.featureId] = allowedBeforeSub.includes(item.featureId);
          });
          setPermissions(noSubPerms);
        }
        setLoadingPermissions(false);
        return;
      }

      if (user?.role === 'receptionist' || user?.role === 'assistant') {
        try {
          const res = await API.get('/staff-permissions/my-permissions');
          setPermissions(res.data);
        } catch (err) {
          console.error('Failed to fetch permissions', err);
          // Set default permissions on error
          setPermissions({
            dashboard: true,
            appointments: true,
            support: true
          });
        }
      }
      setLoadingPermissions(false);
    };

    if (user) {
      fetchPermissions();
      // Register push notifications
      requestNotificationPermission().then(perm => {
        if (perm === 'granted') registerPushNotifications('doctor');
      });
    }
  }, [user]);

  // Handle locked feature click
  const handleLockedFeatureClick = (featureLabel) => {
    if (subscriptionStatus === 'none') {
      toast.error(`Please subscribe to a plan to access ${featureLabel}.`, {
        action: {
          label: 'Subscribe Now',
          onClick: () => navigate('/doctor/subscriptions')
        }
      });
    } else if (subscriptionStatus === 'expired') {
      toast.error(`Your subscription has expired. Please renew to access ${featureLabel}.`, {
        action: {
          label: 'Renew Now',
          onClick: () => navigate('/doctor/subscriptions')
        }
      });
    } else {
      toast.error(`${featureLabel} is not available in your current plan. Please upgrade.`, {
        action: {
          label: 'Upgrade',
          onClick: () => navigate('/doctor/subscriptions')
        }
      });
    }
  };

  // Filter nav items based on permissions
  const filteredNav = navItems.filter(item => {
    if (!permissions) return false;
    return permissions[item.featureId] === true;
  });
  
  // Get locked features for showing with lock icon
  const lockedFeatures = navItems.filter(item => {
    if (!permissions) return false;
    // Always show these in available section when they're not allowed
    const alwaysInAvailable = ['wallet', 'subscription', 'settings', 'support', 'labs_store'];
    if (alwaysInAvailable.includes(item.featureId)) return false;
    // If no subscription or expired, all other features are locked
    if (subscriptionStatus === 'none' || subscriptionStatus === 'expired') {
      return !permissions[item.featureId];
    }
    // For active subscription, show locked premium features
    return !permissions[item.featureId];
  });
  
  // Get subscription status label for UI
  const getSubscriptionBadge = () => {
    if (subscriptionStatus === 'expired') {
      return <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-medium">Expired</span>;
    } else if (subscriptionStatus === 'none') {
      return <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">No Plan</span>;
    } else if (subscriptionStatus === 'active' && subscriptionTier) {
      const tierColors = {
        basic: 'bg-slate-100 text-slate-700 border-slate-300',
        smart: 'bg-blue-100 text-blue-700 border-blue-300',
        advance: 'bg-gradient-to-r from-amber-100 to-orange-100 text-amber-800 border-amber-300'
      };
      return (
        <span className={`text-xs px-2 py-0.5 rounded-full font-semibold border ${tierColors[subscriptionTier] || tierColors.basic}`}>
          {subscriptionTier.charAt(0).toUpperCase() + subscriptionTier.slice(1)}
        </span>
      );
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 overflow-x-hidden" data-testid="app-layout">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex flex-col w-64 fixed inset-y-0 left-0 border-r border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 z-40">
        <div className="p-5 border-b border-slate-200 dark:border-slate-800 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Logo1ClinIcon size={42} />
              {user?.role === 'doctor' && getSubscriptionBadge()}
            </div>
            <div className="flex items-center gap-2">
              <Link 
                to="/doctor/wallet" 
                className="p-2 rounded-lg bg-teal-50 hover:bg-teal-100 text-teal-600 transition-colors"
                title="Wallet"
                data-testid="header-wallet-btn"
              >
                <Wallet className="w-5 h-5" />
              </Link>
              <NotificationBell portalType="doctor" />
            </div>
          </div>
        </div>

        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto min-h-0">
          <Accordion type="multiple" value={openGroups} onValueChange={setOpenGroups} className="space-y-1">
            {navGroupDefs.map(group => {
              const groupItems = filteredNav.filter(item => group.featureIds.includes(item.featureId));
              if (groupItems.length === 0) return null;
              const GroupIcon = group.icon;
              return (
                <AccordionItem key={group.id} value={group.id} className="border-b-0">
                  <AccordionTrigger
                    data-testid={`nav-accordion-${group.id.replace(/_/g, '-')}`}
                    className="py-2 px-3 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 hover:no-underline text-xs font-bold uppercase tracking-wide text-slate-800 dark:text-slate-100"
                  >
                    <span className="flex items-center gap-2">
                      <GroupIcon className="w-4 h-4" />
                      {group.label}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="pb-1 pt-0.5">
                    <div className="space-y-0.5">
                      {groupItems.map(item => {
                        const Icon = item.icon;
                        const active = location.pathname === item.path || location.pathname === item.path.replace('/doctor', '');
                        return (
                          <Link
                            key={item.path}
                            to={item.path}
                            data-testid={`nav-item-${item.featureId.replace(/_/g, '-')}`}
                            className={`flex items-center gap-3 pl-6 pr-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                              active
                                ? 'bg-teal-50 text-teal-700 dark:bg-teal-900/20 dark:text-teal-400 shadow-sm'
                                : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200'
                            }`}
                          >
                            <Icon className="w-[16px] h-[16px]" />
                            {item.label}
                          </Link>
                        );
                      })}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>

          {/* Show locked premium features with lock icon */}
          {user?.role === 'doctor' && lockedFeatures.length > 0 && (
            <>
              <div className="border-t border-slate-200 dark:border-slate-700 my-2 pt-2">
                <p className="text-xs text-slate-400 px-3 mb-1">Premium Features</p>
              </div>
              {lockedFeatures.map(item => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.path}
                    onClick={() => handleLockedFeatureClick(item.label)}
                    data-testid={`nav-locked-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all duration-200"
                  >
                    <Icon className="w-[18px] h-[18px]" />
                    <span className="flex-1 text-left">{item.label}</span>
                    <Lock className="w-3.5 h-3.5" />
                  </button>
                );
              })}
            </>
          )}
        </nav>

        <div className="p-4 border-t border-slate-200 dark:border-slate-800">
          {/* Download App Button */}
          <div className="mb-3">
            <DownloadAppButton 
              variant="outline" 
              size="sm"
              className="w-full border-teal-500 text-teal-600 hover:bg-teal-50 dark:text-teal-400 dark:hover:bg-teal-900/20"
            />
          </div>
          
          <div className="flex items-center gap-3 mb-3 px-1">
            <div className="w-9 h-9 rounded-full bg-teal-100 dark:bg-teal-900/40 flex items-center justify-center">
              <span className="text-sm font-bold text-teal-700 dark:text-teal-400">
                {user?.name?.charAt(0) || 'D'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-900 dark:text-white truncate">{user?.name}</p>
              <p className="text-[11px] text-slate-400 capitalize">{user?.role}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            className="w-full justify-start text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10"
            onClick={handleLogoutClick}
            data-testid="sidebar-logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" /> Sign Out
          </Button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-white/90 dark:bg-slate-900/90 backdrop-blur-lg border-b border-slate-200/50 dark:border-slate-800/50 z-50 flex items-center px-4">
        <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 -ml-2" data-testid="mobile-menu-btn">
          {sidebarOpen ? <X className="w-5 h-5 text-slate-700 dark:text-slate-200" /> : <Menu className="w-5 h-5 text-slate-700 dark:text-slate-200" />}
        </button>
        <div className="flex-1 flex items-center justify-center gap-2">
          <Logo1ClinIcon size={32} />
          {user?.role === 'doctor' && getSubscriptionBadge()}
        </div>
        <div className="flex items-center gap-1">
          <Link 
            to="/doctor/wallet" 
            className="p-2 rounded-lg bg-teal-50 hover:bg-teal-100 text-teal-600 transition-colors"
            data-testid="mobile-wallet-btn"
          >
            <Wallet className="w-5 h-5" />
          </Link>
          <NotificationBell portalType="doctor" />
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-black/40 backdrop-blur-sm" onClick={() => setSidebarOpen(false)}>
          <aside className="w-72 bg-white dark:bg-slate-900 h-full shadow-2xl flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="h-14 flex-shrink-0" /> {/* Spacer for fixed header */}
            <div className="flex-1 overflow-y-auto overscroll-contain">
              <nav className="p-4 pb-0">
                <Accordion type="multiple" defaultValue={[activeGroupId]} className="space-y-1">
                  {navGroupDefs.map(group => {
                    const groupItems = filteredNav.filter(item => group.featureIds.includes(item.featureId));
                    if (groupItems.length === 0) return null;
                    const GroupIcon = group.icon;
                    return (
                      <AccordionItem key={group.id} value={group.id} className="border-b-0">
                        <AccordionTrigger
                          data-testid={`mobile-nav-accordion-${group.id.replace(/_/g, '-')}`}
                          className="py-2.5 px-2 rounded-lg hover:bg-slate-100 hover:no-underline text-xs font-bold uppercase tracking-wide text-slate-800"
                        >
                          <span className="flex items-center gap-2">
                            <GroupIcon className="w-4 h-4" />
                            {group.label}
                          </span>
                        </AccordionTrigger>
                        <AccordionContent className="pb-1 pt-0.5">
                          <div className="space-y-1">
                            {groupItems.map(item => {
                              const Icon = item.icon;
                              const active = location.pathname === item.path;
                              return (
                                <Link key={item.path} to={item.path} onClick={() => setSidebarOpen(false)}
                                  data-testid={`mobile-nav-item-${item.featureId.replace(/_/g, '-')}`}
                                  className={`flex items-center gap-3 pl-6 pr-3 py-3 rounded-lg text-sm font-medium transition-colors ${
                                    active ? 'bg-teal-50 text-teal-700 dark:bg-teal-900/20 dark:text-teal-400' : 'text-slate-600 hover:bg-slate-100 dark:text-slate-400'
                                  }`}>
                                  <Icon className="w-5 h-5" />
                                  {item.label}
                                </Link>
                              );
                            })}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              </nav>
              {/* Sign Out section with padding for safe area */}
              <div className="p-4 pt-2 pb-24 border-t border-slate-100 mt-4">
                <Button variant="ghost" className="w-full justify-start text-slate-400 hover:text-red-500 hover:bg-red-50" onClick={handleLogoutClick}>
                  <LogOut className="w-4 h-4 mr-2" /> Sign Out
                </Button>
              </div>
            </div>
          </aside>
        </div>
      )}

      {/* Mobile Bottom Nav */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 z-50 flex justify-around py-2 px-1 md:px-4 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
        {filteredNav.slice(0, 5).map(item => {
          const Icon = item.icon;
          const active = location.pathname === item.path;
          return (
            <Link key={item.path} to={item.path}
              className={`flex flex-col items-center gap-0.5 px-2 md:px-3 py-1 rounded-lg transition-colors min-w-0 ${
                active ? 'text-teal-700 dark:text-teal-400' : 'text-slate-400'
              }`}>
              <Icon className="w-5 h-5 md:w-6 md:h-6 flex-shrink-0" />
              <span className="text-[9px] md:text-[11px] font-medium truncate max-w-[56px] md:max-w-none">{item.label}</span>
            </Link>
          );
        })}
      </nav>

      {/* Main Content */}
      <main className="lg:ml-64 pt-14 lg:pt-0 pb-24 lg:pb-0 min-h-screen">
        <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto">
          {children}
        </div>
      </main>

      {/* Logout Confirmation Dialog */}
      <Dialog open={showLogoutDialog} onOpenChange={setShowLogoutDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Confirm Logout
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to sign out? You will need to log in again to access your account.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button variant="outline" onClick={cancelLogout}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmLogout} data-testid="confirm-logout-btn">
              <LogOut className="w-4 h-4 mr-2" /> Sign Out
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
